/**
 * AISystem — OWNER: ai builder (packages/sim/src/ai/**).
 * NPC brains: aggro radius + social pulls (+patrols: per-mob aggro with line of sight, links,
 * call-for-help and leash for opt-in packs; idle/wander/waypoint/formation movement in roam.ts), threat victim selection, EventMap/SmartAI ability
 * timers cast through ctx.sys.casting, chase / keep-range movement, evade on an empty threat
 * list, and boss encounters (start, HP phases, enrage, kill/wipe).
 */
import type {
  AbilityId,
  AISystem,
  AuraDef,
  CastFailReason,
  EncounterDef,
  EncounterState,
  PackDef,
  PackId,
  SimContext,
  Unit,
  UnitId,
  Vec2,
} from '../contracts';
import { ASSIST_DELAY_MS, ASSIST_RADIUS, EVADE_CHECK_MS, FIELD_AI_SLEEP_YD, LEASH_RADIUS, STEALTH_DETECT_RADIUS, THREAT_RESELECT_MS } from '../contracts';
import { roam, roamSpawn } from './roam'; // +patrols
import { fieldPackDef, fieldSpawnDef } from '../field/defs';
import { fleePoint } from '../field/loot'; // +field
import { ensureThreatEntry, isHostileTo, pickVictim, pruneThreat, resolveAbilityTarget, threatList } from './targeting';
import { ENRAGE_KEY, firstDelay, isReservedKey, repeatDelay, type TimerRanges } from './timers';
import * as mech from './mechanics/index'; // +encounters

/** +basekits: the unit holds a stealth aura (Lurk). */
const isStealthed = (ctx: SimContext, u: Unit): boolean => u.auras.some((a) => ctx.content.aura(a.auraId)?.flags?.stealth);

/** Aggro radius for NPCs that are not part of a pack (summons, manual spawns). */
export const DEFAULT_AGGRO_RADIUS = 10;
/** A chasing NPC re-paths when its victim has moved this far from the current destination. */
const CHASE_REPATH_DIST = 1.5;
/** An evading NPC within this distance of home snaps to it and goes idle. */
const HOME_ARRIVE_DIST = 0.5;
/** Ability with no valid target right now is retried after this long. */
const NO_TARGET_RETRY_MS = 1000;
/** Cast failures worth retrying next update (the event stays due, so the cycle drifts). */
const TRANSIENT_FAILS: ReadonlySet<CastFailReason> = new Set<CastFailReason>([
  'on_gcd',
  'casting',
  'school_locked',
  'silenced',
  'cannot_act',
  'moving',
  'out_of_range',
  'not_facing',
  'on_cooldown',
  'no_resource',
]);

/** Fallback hard-enrage aura when content does not define `ENRAGE_AURA_ID`. */
export const ENRAGE_AURA_ID = 'npc.hard_enrage';
const FALLBACK_ENRAGE: AuraDef = {
  id: ENRAGE_AURA_ID,
  name: 'Enrage',
  kind: 'buff',
  durationMs: 0,
  stacking: 'ignore',
  mods: [
    { stat: 'damageDonePct', value: 9 },
    { stat: 'hastePct', value: 1 },
  ],
};

const angleTo = (a: Vec2, b: Vec2): number => Math.atan2(b.y - a.y, b.x - a.x);

export function createAISystem(): AISystem {
  /** Derived cache: pack defs by dungeon → pack id. */
  const packIndex = new Map<string, Map<PackId, PackDef>>();

  function packDef(ctx: SimContext, packId: PackId | null): PackDef | undefined {
    if (!packId) return undefined;
    let idx = packIndex.get(ctx.world.dungeonId);
    if (!idx) {
      idx = new Map((ctx.content.dungeon(ctx.world.dungeonId)?.packs ?? []).map((p) => [p.id, p]));
      packIndex.set(ctx.world.dungeonId, idx);
    }
    return idx.get(packId);
  }

  /** +field: the unit's pack def, or the synthetic loose def of its field spawn point. */
  function defOf(ctx: SimContext, u: Unit | undefined): PackDef | undefined {
    if (!u) return undefined;
    if (u.packId) return packDef(ctx, u.packId);
    return u.spawnId ? fieldPackDef(ctx.content, ctx.world.dungeonId, u.spawnId) : undefined;
  }

  const isTrash = (ctx: SimContext, npc: Unit): boolean => npc.tier !== 'boss' && ctx.content.npc(npc.npcId ?? '')?.tier !== 'boss';

  const packMembers = (ctx: SimContext, packId: PackId): Unit[] =>
    (ctx.world.packs[packId]?.memberIds ?? []).map((id) => ctx.world.units[id]).filter((u): u is Unit => !!u);

  const activeEncounter = (ctx: SimContext): EncounterState | null => {
    const e = ctx.world.encounter;
    return e && e.result === null ? e : null;
  };

  // ---- abilities ---------------------------------------------------------------

  /** Enemy abilities this NPC may use now (encounter phase list for bosses, NpcDef list otherwise). */
  function activeAbilities(ctx: SimContext, npc: Unit): AbilityId[] {
    const def = ctx.content.npc(npc.npcId ?? '');
    if (!def) return [];
    const enc = activeEncounter(ctx);
    if (!enc || npc.encounterId !== enc.encounterId || !enc.bossIds.includes(npc.id)) return def.abilities;
    const encDef = ctx.content.encounter(enc.encounterId);
    const phase = encDef?.phases[enc.phaseIndex];
    if (!phase) return def.abilities;
    const bossDefs = enc.bossIds.map((id) => ctx.content.npc(ctx.world.units[id]?.npcId ?? ''));
    const firstAlive = enc.bossIds.find((id) => ctx.world.units[id]?.alive);
    // A phase ability is used by the boss(es) that list it; unowned ones go to the first alive boss.
    return phase.abilities.filter((id) => def.abilities.includes(id) || (npc.id === firstAlive && !bossDefs.some((d) => d?.abilities.includes(id))));
  }

  /** (Re)start EventMap timers for `ids` from their `first` delay; drop timers not in the list. */
  function schedule(ctx: SimContext, npc: Unit, ids: AbilityId[]): void {
    const ai = npc.ai!;
    const trash = isTrash(ctx, npc);
    const next: Record<AbilityId, number> = {};
    for (const k of Object.keys(ai.timers)) if (isReservedKey(k)) next[k] = ai.timers[k]!;
    for (const id of ids) {
      const ab = ctx.content.enemyAbility(id);
      if (ab) next[id] = ctx.now + firstDelay(ctx.rng, ab.timer as TimerRanges, trash);
    }
    ai.timers = next;
  }

  /** EventMap semantics: at most one ability per update, none while casting; Repeat counts from now. */
  function tryAbilities(ctx: SimContext, npc: Unit): void {
    const ai = npc.ai!;
    const ids = activeAbilities(ctx, npc);
    const due = ids
      .map((id, order) => ({ id, order, ab: ctx.content.enemyAbility(id) }))
      .filter((d) => d.ab && ai.timers[d.id] !== undefined && ai.timers[d.id]! <= ctx.now)
      .sort((a, b) => (b.ab!.priority ?? 0) - (a.ab!.priority ?? 0) || a.order - b.order);
    const trash = isTrash(ctx, npc);
    const hpFrac = npc.hp / npc.maxHp;
    for (const { id, ab } of due) {
      const def = ab!;
      if (!mech.abilityUsable(ctx, npc, def, hpFrac)) continue; // +encounters: HP window (+affix), key/affix gates, once
      const tgt = resolveAbilityTarget(ctx, npc, def);
      if (!tgt) {
        ai.timers[id] = ctx.now + NO_TARGET_RETRY_MS;
        continue;
      }
      const target = tgt.targetId ? ctx.world.units[tgt.targetId] : undefined;
      if (target && target.id !== npc.id) {
        npc.facing = angleTo(npc.pos, target.pos);
        if ((def.range ?? 0) > 0 && isHostileTo(npc, target) && !ctx.sys.spatial.inRange(ctx.world, npc.id, target.id, def.range!)) continue;
      }
      const res = ctx.sys.casting.tryCast(ctx, npc.id, id, tgt.targetId, tgt.point);
      if (res.ok) {
        if (def.timer.every > 0) ai.timers[id] = ctx.now + repeatDelay(ctx.rng, def.timer as TimerRanges, trash);
        else delete ai.timers[id];
        mech.onAbilityUsed(ctx, npc, def); // +encounters: chains, once, facing lock
        if (npc.cast && !def.canCastWhileMoving) stopMoving(npc);
        return;
      }
      if (!TRANSIENT_FAILS.has(res.reason)) {
        if (def.timer.every > 0) ai.timers[id] = ctx.now + repeatDelay(ctx.rng, def.timer as TimerRanges, trash);
        else delete ai.timers[id];
      }
    }
  }

  // ---- movement ----------------------------------------------------------------

  function stopMoving(u: Unit): void {
    if (u.move && u.move.mode !== 'forced') u.move = null;
  }

  function moveToward(ctx: SimContext, npc: Unit, p: Vec2): void {
    if (npc.move?.mode === 'dest' && ctx.sys.spatial.distance(npc.move.dest, p) <= CHASE_REPATH_DIST) return;
    if (!ctx.sys.spatial.setDestination(ctx, npc.id, p)) stopMoving(npc);
  }

  /** Melee NPCs run into melee range; casters with preferredRange hold that distance. */
  function chase(ctx: SimContext, npc: Unit, victim: Unit): void {
    const sp = ctx.sys.spatial;
    const npcDef = ctx.content.npc(npc.npcId ?? '');
    // +field: the treasure sprite never fights: it runs from whoever it is "fighting".
    if (npcDef?.flees) return moveToward(ctx, npc, fleePoint(ctx, npc, victim.pos));
    const pref = npcDef?.preferredRange;
    const settled = pref !== undefined && pref > 0 ? sp.rangeBetween(ctx.world, npc.id, victim.id) <= pref : sp.inMeleeRange(ctx.world, npc.id, victim.id);
    if (settled) {
      stopMoving(npc);
      npc.facing = angleTo(npc.pos, victim.pos);
    } else moveToward(ctx, npc, victim.pos);
  }

  function patrol(ctx: SimContext, npc: Unit): void {
    // +field: field mobs wander (or idle) around their spawn point.
    const spawn = npc.spawnId ? fieldSpawnDef(ctx.content, ctx.world.dungeonId, npc.spawnId) : undefined;
    if (spawn) {
      roamSpawn(ctx, npc, spawn.movement ?? { type: 'wander', radius: spawn.radius }, spawn.pos);
      return;
    }
    const def = packDef(ctx, npc.packId);
    const route = def?.patrol;
    if (!def || !route || route.length === 0) {
      roam(ctx, npc, def); // +patrols
      return;
    }
    const ai = npc.ai!;
    ai.mode = 'patrol';
    if (npc.move) return;
    // Members keep their spawn offset from the pack's first spawn while walking the loop.
    const anchor = def.spawns[0]?.pos ?? npc.home;
    const at = (i: number): Vec2 => {
      const wp = route[i % route.length]!;
      return { x: wp.x + npc.home.x - anchor.x, y: wp.y + npc.home.y - anchor.y };
    };
    if (ctx.sys.spatial.distance(npc.pos, at(ai.patrolIndex)) <= HOME_ARRIVE_DIST) ai.patrolIndex = (ai.patrolIndex + 1) % route.length;
    ctx.sys.spatial.setDestination(ctx, npc.id, at(ai.patrolIndex));
  }

  // ---- combat entry / exit -----------------------------------------------------

  function enterCombat(ctx: SimContext, npc: Unit, attackerId: UnitId): void {
    const ai = npc.ai;
    const attacker = ctx.world.units[attackerId];
    if (!ai || !npc.alive || !isHostileTo(npc, attacker)) return;
    if (ai.mode === 'evading') return;
    ensureThreatEntry(ctx, npc, attackerId);
    attacker.inCombat = true;
    if (ai.mode === 'combat') return;
    // +patrols: roaming mobs return to where they were pulled (TC sets home at the combat start) and resume.
    if (ai.mode === 'patrol' && !packDef(ctx, npc.packId)?.patrol) {
      ai.roam ??= { nextAt: ctx.now, dir: 1 };
      ai.roam.resumeAt = { x: npc.pos.x, y: npc.pos.y };
    }
    ai.mode = 'combat';
    ai.engagedAt = ctx.now;
    ai.nextEvadeCheckAt = ctx.now + EVADE_CHECK_MS;
    npc.inCombat = true;
    stopMoving(npc);
    schedule(ctx, npc, activeAbilities(ctx, npc));
    mech.onEngage(ctx, npc); // +encounters: NPC passive auras, damage links
    if (npc.packId) {
      const ps = ctx.world.packs[npc.packId];
      if (ps && ps.state === 'idle') {
        ps.state = 'engaged';
        ps.engagedAt = ctx.now;
      }
    }
    pickVictim(ctx, npc);
    npc.threat!.nextReselectAt = ctx.now + THREAT_RESELECT_MS;
    const v = npc.targetId ? ctx.world.units[npc.targetId] : undefined;
    if (v) npc.facing = angleTo(npc.pos, v.pos);
  }

  /**
   * Social aggro: the pulled member brings in every pack member within socialRadius of an
   * already-pulled member (chained). Without socialRadius (or no origin) the whole pack joins.
   */
  function pullPack(ctx: SimContext, packId: PackId, attackerId: UnitId, originId: UnitId | null): void {
    const members = packMembers(ctx, packId).filter((u) => u.alive && u.ai);
    const social = packDef(ctx, packId)?.socialRadius;
    let pulled: Unit[];
    const origin = originId ? members.find((m) => m.id === originId) : undefined;
    if (!origin || social === undefined) pulled = members;
    else {
      pulled = [origin];
      const rest = members.filter((m) => m !== origin);
      for (let i = 0; i < pulled.length; i++) {
        const from = pulled[i]!;
        for (let j = rest.length - 1; j >= 0; j--) {
          if (ctx.sys.spatial.distance(from.pos, rest[j]!.pos) <= social) pulled.push(...rest.splice(j, 1));
        }
      }
      pulled.sort((a, b) => members.indexOf(a) - members.indexOf(b));
    }
    for (const m of pulled) enterCombat(ctx, m, attackerId);
  }

  function engage(ctx: SimContext, npc: Unit, attackerId: UnitId): void {
    const def = defOf(ctx, npc); // +field
    if (def?.link) socialEngage(ctx, npc, def, attackerId, false); // +patrols
    else if (npc.packId && ctx.world.packs[npc.packId]) pullPack(ctx, npc.packId, attackerId, npc.id);
    else enterCombat(ctx, npc, attackerId);
  }

  // ---- +patrols: WoW social aggro (opt-in packs) ----------------------------------

  const canJoin = (u: Unit | undefined): u is Unit => !!u?.alive && !!u.ai && (u.ai.mode === 'idle' || u.ai.mode === 'patrol');

  /**
   * A linked pack comes as one (TC formation groupAI: members and leader assist each other); a
   * loose pack only sends the mob that was pulled. Each mob entering combat this way calls for help
   * once; mobs that came to help (and their links) do not call again (TC SetNoCallAssistance).
   */
  function socialEngage(ctx: SimContext, npc: Unit, def: PackDef, attackerId: UnitId, viaHelp: boolean): void {
    const group = def.link === 'linked' && npc.packId ? packMembers(ctx, npc.packId).filter((m) => m === npc || canJoin(m)) : [npc];
    const joined: Unit[] = [];
    for (const m of group) {
      const was = m.ai?.mode;
      enterCombat(ctx, m, attackerId);
      if (m.ai && m.ai.mode === 'combat' && was !== 'combat') joined.push(m);
    }
    // Everyone linked is in first, so the calls only reach mobs outside the group.
    for (const m of joined) {
      if (viaHelp) m.ai!.calledHelp = true;
      else callForHelp(ctx, m, def);
    }
  }

  /** TC Creature::CallAssistance: idle opt-in trash within the radius and in line of sight joins after ASSIST_DELAY_MS. */
  function callForHelp(ctx: SimContext, caller: Unit, def: PackDef): void {
    const ai = caller.ai!;
    if (ai.calledHelp) return;
    ai.calledHelp = true;
    const radius = def.assistRadius ?? ASSIST_RADIUS;
    const sp = ctx.sys.spatial;
    const helperIds: UnitId[] = [];
    for (const id of ctx.world.unitOrder) {
      const h = ctx.world.units[id];
      if (!canJoin(h) || h === caller || h.kind !== 'npc' || h.faction !== caller.faction || h.summonerId) continue;
      const hDef = defOf(ctx, h); // +field
      if (!hDef?.link || hDef.encounterId || !isTrash(ctx, h)) continue;
      if (h.spawnId && hDef.aggroRadius <= 0) continue; // +field: passive field mobs never come to help
      if (sp.distance(caller.pos, h.pos) > radius) continue;
      if (sp.lineOfSight && !sp.lineOfSight(ctx.world, caller.pos, h.pos)) continue;
      helperIds.push(id);
    }
    const attackerId = caller.threat?.victimId ?? Object.keys(caller.threat?.entries ?? {})[0];
    if (helperIds.length > 0 && attackerId) ai.assist = { at: ctx.now + ASSIST_DELAY_MS, helperIds, attackerId };
  }

  /** Due calls for help: helpers still idle attack the caller's victim (or the original attacker). */
  function resolveAssist(ctx: SimContext, caller: Unit): void {
    const call = caller.ai?.assist;
    if (!call || ctx.now < call.at) return;
    caller.ai!.assist = null;
    const victimId = caller.alive && caller.threat?.victimId ? caller.threat.victimId : call.attackerId;
    const victim = ctx.world.units[victimId];
    if (!victim?.alive) return;
    for (const id of call.helperIds) {
      const h = ctx.world.units[id];
      const hDef = defOf(ctx, h); // +field
      if (!canJoin(h) || !hDef?.link || !isHostileTo(h, victim)) continue;
      socialEngage(ctx, h, hDef, victimId, true);
    }
  }

  function evade(ctx: SimContext, npcId: UnitId): void {
    const u = ctx.world.units[npcId];
    if (!u || !u.alive || !u.ai) return;
    if (u.cast) ctx.sys.casting.cancelCast(ctx, u.id, 'target_lost');
    for (const a of [...u.auras]) ctx.sys.combat.removeAura(ctx, u.id, { iid: a.iid }, 'evade');
    u.auras = [];
    if (u.threat) {
      u.threat.entries = {};
      u.threat.victimId = null;
      u.threat.taunt = null;
      u.threat.fixate = null;
      u.threat.tauntDrCount = 0;
      u.threat.nextReselectAt = 0;
    }
    u.targetId = null;
    u.hp = u.maxHp;
    u.cooldowns = {};
    u.gcdUntil = 0;
    u.lockouts = {};
    u.dr = {};
    u.queued = null;
    u.recentDamage = [];
    u.inCombat = false;
    if (u.swing) u.swing.nextAt = 0;
    u.ai.mode = 'evading';
    u.ai.timers = {};
    u.ai.phaseIndex = 0;
    u.ai.engagedAt = null;
    // Summons vanish with their summoner's reset.
    for (const id of [...ctx.world.unitOrder]) {
      const s = ctx.world.units[id];
      if (s && s.summonerId === u.id && s.alive) ctx.ops.despawn(id);
    }
    if (u.move?.mode === 'forced') u.move = null;
    u.ai.assist = null;
    if (!ctx.sys.spatial.setDestination(ctx, u.id, returnPoint(u))) finishEvade(ctx, u);
  }

  /** +patrols: roaming mobs walk back to where they were pulled; everyone else to its spawn. */
  const returnPoint = (u: Unit): Vec2 => u.ai?.roam?.resumeAt ?? u.home;

  function finishEvade(ctx: SimContext, u: Unit): void {
    // Backstop: nothing gained during the walk home survives the reset.
    if (u.threat) {
      u.threat.entries = {};
      u.threat.victimId = null;
      u.threat.taunt = null;
      u.threat.fixate = null;
    }
    u.targetId = null;
    u.inCombat = false;
    const back = returnPoint(u);
    u.pos = { x: back.x, y: back.y };
    u.facing = u.homeFacing;
    u.move = null;
    u.ai!.mode = 'idle';
    u.ai!.calledHelp = false;
    u.ai!.assist = null;
    // +patrols: waypoint patrols resume where they left off; the legacy loop restarts.
    if (u.ai!.roam) {
      u.ai!.roam.resumeAt = null;
      u.ai!.roam.going = false;
      u.ai!.roam.nextAt = ctx.now;
    } else u.ai!.patrolIndex = 0;
    if (u.packId) {
      const ps = ctx.world.packs[u.packId];
      if (ps && ps.state === 'engaged' && packMembers(ctx, u.packId).every((m) => !m.alive || m.ai?.mode === 'idle' || m.ai?.mode === 'patrol')) {
        ps.state = 'idle';
        ps.engagedAt = null;
      }
    }
  }

  // ---- per-mode updates --------------------------------------------------------

  function checkAggro(ctx: SimContext, npc: Unit): boolean {
    // Summons join their summoner's fight.
    const summoner = npc.summonerId ? ctx.world.units[npc.summonerId] : undefined;
    if (summoner?.alive && summoner.ai?.mode === 'combat' && summoner.threat?.victimId) {
      enterCombat(ctx, npc, summoner.threat.victimId);
      for (const u of threatList(ctx, summoner)) ensureThreatEntry(ctx, npc, u.id);
      return true;
    }
    const def = defOf(ctx, npc); // +field
    const radius = def?.aggroRadius ?? DEFAULT_AGGRO_RADIUS;
    if (radius <= 0) return false;
    // +patrols: opt-in packs only notice what they can see.
    const los = def?.link ? ctx.sys.spatial.lineOfSight : undefined;
    for (const pid of ctx.world.partyIds) {
      const p = ctx.world.units[pid];
      // +basekits: a stealthed hero (Lurk) is only noticed up close.
      let r = p && isStealthed(ctx, p) ? Math.min(radius, STEALTH_DETECT_RADIUS) : radius;
      // +zone: open-world mobs notice heroes above their level later (WoW-style), and below sooner.
      if (npc.spawnId && p && p.level !== undefined && npc.level !== undefined) r = Math.max(3, Math.min(radius + 3, r - Math.max(-4, Math.min(8, p.level - npc.level)) * 0.7));
      if (isHostileTo(npc, p) && ctx.sys.spatial.distance(npc.pos, p.pos) <= r && (!los || los(ctx.world, npc.pos, p.pos))) {
        engage(ctx, npc, pid);
        return true;
      }
    }
    return false;
  }

  function updateCombat(ctx: SimContext, npc: Unit): void {
    const ai = npc.ai!;
    const t = npc.threat!;
    pruneThreat(ctx, npc);
    if (threatList(ctx, npc).length === 0) {
      evade(ctx, npc.id);
      return;
    }
    if (ctx.now >= ai.nextEvadeCheckAt) {
      ai.nextEvadeCheckAt = ctx.now + EVADE_CHECK_MS;
      if (outsideArena(ctx, npc)) {
        evade(ctx, npc.id);
        return;
      }
      // +patrols: opt-in trash leashes once dragged too far from where it was pulled.
      if (leashed(ctx, npc)) {
        evade(ctx, npc.id);
        return;
      }
      // +tutorial: dummies reset after a while without threat (NpcDef.idleResetMs).
      if (idleTooLong(ctx, npc)) {
        evade(ctx, npc.id);
        return;
      }
    }
    if (!t.victimId || ctx.now >= t.nextReselectAt) {
      pickVictim(ctx, npc);
      t.nextReselectAt = ctx.now + THREAT_RESELECT_MS;
    }
    const cs = ctx.sys.combat.controlState(ctx, npc.id);
    if (!cs.canAct) return;
    if (!npc.cast && cs.canCast) tryAbilities(ctx, npc);
    if (npc.cast) {
      const ab = ctx.content.ability(npc.cast.abilityId);
      if (!ab?.canCastWhileMoving) stopMoving(npc);
      return;
    }
    const victim = t.victimId ? ctx.world.units[t.victimId] : undefined;
    if (!victim) return;
    npc.targetId = victim.id;
    if (cs.canMove) chase(ctx, npc, victim);
    else npc.facing = angleTo(npc.pos, victim.pos);
  }

  function leashed(ctx: SimContext, npc: Unit): boolean {
    // +field: field mobs evade once dragged farther than FieldDef.leashRadius from their spawn point.
    if (npc.spawnId) {
      const field = ctx.content.dungeon(ctx.world.dungeonId)?.field;
      const spawn = field ? fieldSpawnDef(ctx.content, ctx.world.dungeonId, npc.spawnId) : undefined;
      // +zone: patrols leash from where they were pulled (their route can be long).
      if (field && spawn) return ctx.sys.spatial.distance(npc.pos, spawn.movement?.type === 'patrol' ? returnPoint(npc) : spawn.pos) > field.leashRadius;
    }
    const def = packDef(ctx, npc.packId);
    if (!def?.link || def.encounterId || !isTrash(ctx, npc)) return false;
    return ctx.sys.spatial.distance(npc.pos, returnPoint(npc)) > LEASH_RADIUS;
  }

  /** +tutorial: NpcDef.idleResetMs elapsed since the last threat any hostile generated. */
  function idleTooLong(ctx: SimContext, npc: Unit): boolean {
    const idle = npc.npcId ? ctx.content.npc(npc.npcId)?.idleResetMs : undefined;
    if (!idle || idle <= 0 || !npc.threat) return false;
    let last = -Infinity;
    for (const id in npc.threat.entries) last = Math.max(last, npc.threat.entries[id]!.lastAt);
    return ctx.now - last >= idle;
  }

  function outsideArena(ctx: SimContext, npc: Unit): boolean {
    const enc = activeEncounter(ctx);
    if (!enc || npc.encounterId !== enc.encounterId) return false;
    const a = ctx.content.encounter(enc.encounterId)?.arena;
    if (!a) return false;
    const p = npc.pos;
    return p.x < a.x || p.y < a.y || p.x > a.x + a.w || p.y > a.y + a.h;
  }

  function updateEvade(ctx: SimContext, npc: Unit): void {
    npc.hp = npc.maxHp;
    if (!npc.move || ctx.sys.spatial.distance(npc.pos, npc.home) <= HOME_ARRIVE_DIST) finishEvade(ctx, npc);
  }

  // ---- encounters --------------------------------------------------------------

  function startEncounter(ctx: SimContext, packId: PackId, def: EncounterDef): void {
    const members = packMembers(ctx, packId).filter((u) => u.alive);
    const bosses = members.filter((u) => u.npcId && def.bossNpcIds.includes(u.npcId));
    const enc: EncounterState = {
      encounterId: def.id,
      packId,
      bossIds: (bosses.length > 0 ? bosses : members).map((u) => u.id),
      startedAt: ctx.now,
      phaseIndex: 0,
      result: null,
    };
    ctx.world.encounter = enc;
    for (const m of members) m.encounterId = def.id;
    ctx.emit({ type: 'encounter_start', encounterId: def.id, name: def.name });
    // Boss fights put the whole (alive) party in combat with every member.
    for (const m of members) {
      for (const pid of ctx.world.partyIds) {
        const p = ctx.world.units[pid];
        if (isHostileTo(m, p)) enterCombat(ctx, m, pid);
      }
    }
    enterPhase(ctx, enc, def, 0);
  }

  function enterPhase(ctx: SimContext, enc: EncounterState, def: EncounterDef, index: number): void {
    enc.phaseIndex = index;
    const bosses = enc.bossIds.map((id) => ctx.world.units[id]).filter((u): u is Unit => !!u?.alive && !!u.ai);
    for (const b of bosses) {
      b.ai!.phaseIndex = index;
      schedule(ctx, b, activeAbilities(ctx, b));
      mech.onTimersScheduled(ctx, b); // +encounters
    }
    const phase = def.phases[index];
    const first = bosses[0];
    if (phase?.onEnter && first) ctx.sys.combat.applyEffects(ctx, phase.onEnter, { sourceId: first.id, abilityId: def.id, targetId: first.id, point: null });
    if (phase?.intermission) mech.startIntermission(ctx, enc, def, index); // +encounters
  }

  function endEncounter(ctx: SimContext, enc: EncounterState, kill: boolean): void {
    const def = ctx.content.encounter(enc.encounterId);
    enc.result = kill ? 'kill' : 'wipe';
    ctx.emit({ type: 'encounter_end', encounterId: enc.encounterId, name: def?.name ?? enc.encounterId, kill });
    if (kill && !ctx.world.encountersDone.includes(enc.encounterId)) ctx.world.encountersDone.push(enc.encounterId);
    ctx.world.encounter = null;
    // A kill ends the fight: adds the bosses summoned (Uncoiled Writhes) vanish with them.
    if (kill) {
      for (const id of [...ctx.world.unitOrder]) {
        const s = ctx.world.units[id];
        if (s?.alive && s.summonerId && enc.bossIds.includes(s.summonerId)) ctx.ops.despawn(id);
      }
      const ps = ctx.world.packs[enc.packId];
      if (ps && packMembers(ctx, enc.packId).every((m) => !m.alive)) ps.state = 'dead';
    }
    if (!kill) for (const m of packMembers(ctx, enc.packId)) if (m.alive) evade(ctx, m.id);
  }

  function applyEnrage(ctx: SimContext, boss: Unit): void {
    boss.ai!.timers[ENRAGE_KEY] = ctx.now;
    const aura = ctx.content.aura(ENRAGE_AURA_ID) ?? FALLBACK_ENRAGE;
    ctx.sys.combat.applyEffects(ctx, [{ type: 'applyAura', to: 'self', aura }], { sourceId: boss.id, abilityId: ENRAGE_AURA_ID, targetId: boss.id, point: null });
  }

  /** +zone: no living player unit within FIELD_AI_SLEEP_YD (checked against every party unit). */
  function asleep(ctx: SimContext, u: Unit): boolean {
    const w = ctx.world;
    for (const id of w.partyIds) {
      const h = w.units[id];
      if (h && !h.departed && Math.abs(h.pos.x - u.pos.x) < FIELD_AI_SLEEP_YD && Math.abs(h.pos.y - u.pos.y) < FIELD_AI_SLEEP_YD) return false;
    }
    if (u.move && u.move.mode !== 'forced') u.move = null;
    return true;
  }

  // ---- the system ----------------------------------------------------------------

  const sys: AISystem = {
    update(ctx) {
      for (const id of [...ctx.world.unitOrder]) {
        const u = ctx.world.units[id];
        if (!u || u.kind !== 'npc' || !u.ai) continue;
        if (u.ai.assist) resolveAssist(ctx, u); // +patrols: help arrives even if the caller already died
        if (!u.alive) {
          u.ai.mode = 'dead';
          continue;
        }
        switch (u.ai.mode) {
          case 'idle':
          case 'patrol':
            // +zone: a field mob with no hero anywhere near sleeps (big open maps: most of the zone is empty of players).
            if (u.spawnId && asleep(ctx, u)) break;
            if (!checkAggro(ctx, u)) patrol(ctx, u);
            break;
          case 'combat':
            updateCombat(ctx, u);
            break;
          case 'evading':
            updateEvade(ctx, u);
            break;
          case 'dead':
            break;
        }
      }
      ctx.sys.mechanics?.update(ctx); // +encounters: boss/enemy mechanics tick
    },

    engagePack(ctx, packId, attackerId) {
      pullPack(ctx, packId, attackerId, null);
    },

    onAttacked(ctx, npcId, attackerId) {
      const npc = ctx.world.units[npcId];
      const attacker = ctx.world.units[attackerId];
      // Dead players can't pull (lingering zones/DoTs from a corpse don't re-engage an evading boss).
      if (!npc?.alive || !npc.ai || !attacker?.alive || !isHostileTo(npc, attacker)) return;
      if (npc.ai.mode === 'evading') return;
      if (npc.ai.mode === 'combat') enterCombat(ctx, npc, attackerId);
      else engage(ctx, npc, attackerId);
    },

    onDeath(ctx, unitId) {
      const w = ctx.world;
      const dead = w.units[unitId];
      if (dead?.kind === 'npc') mech.onUnitDeath(ctx, unitId); // +encounters: on-death effects
      if (dead?.ai) {
        dead.ai.mode = 'dead';
        dead.move = null;
        if (dead.packId) {
          const ps = w.packs[dead.packId];
          const members = packMembers(ctx, dead.packId);
          if (ps && members.every((m) => !m.alive)) ps.state = 'dead';
          // +patrols: a split pull of a loose pack is over: the rest of the pack is idle again.
          else if (ps && ps.state === 'engaged' && packDef(ctx, dead.packId)?.link && members.every((m) => !m.alive || m.ai?.mode === 'idle' || m.ai?.mode === 'patrol')) {
            ps.state = 'idle';
            ps.engagedAt = null;
          }
        }
      }
      for (const id of w.unitOrder) {
        const t = w.units[id]?.threat;
        if (!t) continue;
        delete t.entries[unitId];
        if (t.victimId === unitId) t.victimId = null;
        if (t.taunt?.by === unitId) t.taunt = null;
        if (t.fixate?.on === unitId) t.fixate = null;
      }
      const enc = activeEncounter(ctx);
      if (enc && enc.bossIds.includes(unitId) && enc.bossIds.every((id) => !w.units[id]?.alive)) endEncounter(ctx, enc, true);
    },

    evade,

    selectVictim(ctx, npcId) {
      const npc = ctx.world.units[npcId];
      return npc ? pickVictim(ctx, npc) : null;
    },

    updateEncounter(ctx) {
      const w = ctx.world;
      const enc = activeEncounter(ctx);
      if (!enc) {
        for (const id of w.unitOrder) {
          const u = w.units[id];
          if (!u?.alive || u.ai?.mode !== 'combat' || !u.packId) continue;
          const encId = packDef(ctx, u.packId)?.encounterId;
          if (!encId || w.encountersDone.includes(encId)) continue;
          const def = ctx.content.encounter(encId);
          if (def) {
            startEncounter(ctx, u.packId, def);
            return;
          }
        }
        return;
      }
      const def = ctx.content.encounter(enc.encounterId);
      const bosses = enc.bossIds.map((id) => w.units[id]).filter((u): u is Unit => !!u);
      if (bosses.every((b) => !b.alive)) {
        endEncounter(ctx, enc, true);
        return;
      }
      const partyAlive = w.partyIds.some((id) => w.units[id]?.alive);
      const engaged = packMembers(ctx, enc.packId).some((m) => m.alive && m.ai?.mode === 'combat');
      if (!partyAlive || !engaged) {
        endEncounter(ctx, enc, false);
        return;
      }
      if (!def) return;
      // Phases advance on combined boss HP (works for single bosses and councils).
      const hp = bosses.reduce((s, b) => s + (b.alive ? b.hp : 0), 0);
      const maxHp = bosses.reduce((s, b) => s + b.maxHp, 0);
      const frac = maxHp > 0 ? hp / maxHp : 0;
      // +encounters: an intermission phase ends by its own rules (no HP threshold of its own).
      if (def.phases[enc.phaseIndex]?.intermission && mech.updateIntermission(ctx, enc, def)) enterPhase(ctx, enc, def, enc.phaseIndex + 1);
      let idx = enc.phaseIndex;
      while (idx < def.phases.length - 1 && def.phases[idx]!.untilHpPct !== undefined && frac < def.phases[idx]!.untilHpPct!) idx++;
      if (idx !== enc.phaseIndex) enterPhase(ctx, enc, def, idx);
      if (def.enrageMs !== undefined && ctx.now - enc.startedAt >= def.enrageMs) {
        for (const b of bosses) if (b.alive && b.ai && b.ai.timers[ENRAGE_KEY] === undefined) applyEnrage(ctx, b);
      }
    },
  };
  return sys;
}
